// Simple API service for horoscope, aarti, and chalisa management
export const API_BASE_URL = 'https://pooja-path-1.onrender.com';
const RASHI_API_URL = `${API_BASE_URL}/api/rashi`;
const AARTI_API_URL = `${API_BASE_URL}/api/aarti`;
const CHALISA_API_URL = `${API_BASE_URL}/api/chalisa`;

export const horoscopeAPI = {
  async testConnection() {
    try {
      const response = await fetch(`${RASHI_API_URL}/health/`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async getAllHoroscopes() {
    try {
      const response = await fetch(`${RASHI_API_URL}/horoscope/admin/`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      // Backend returns an array directly
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.log('Backend not available, using localStorage fallback');
      // Fallback to localStorage
      const localData = localStorage.getItem('horoscopes') || '[]';
      return JSON.parse(localData);
    }
  },

  async createHoroscope(horoscopeData) {
    try {
      const response = await fetch(`${RASHI_API_URL}/horoscope/admin/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(horoscopeData),
      });
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      // Fallback to localStorage
      const newHoroscope = { ...horoscopeData, _id: Date.now().toString() };
      const existing = JSON.parse(localStorage.getItem('horoscopes') || '[]');
      existing.push(newHoroscope);
      localStorage.setItem('horoscopes', JSON.stringify(existing));
      return { success: false, data: newHoroscope, error: error.message };
    }
  },

  async deleteHoroscope(id) {
    try {
      await fetch(`${RASHI_API_URL}/horoscope/${id}/`, { method: 'DELETE' });
      return { success: true };
    } catch (error) {
      // Fallback to localStorage
      const existing = JSON.parse(localStorage.getItem('horoscopes') || '[]');
      const filtered = existing.filter(h => h._id !== id);
      localStorage.setItem('horoscopes', JSON.stringify(filtered));
      return { success: false, error: error.message };
    }
  }
};

export const aartiAPI = {
  async getAllAartis() {
    try {
      const response = await fetch(`${AARTI_API_URL}/list/`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.log('Backend not available, using localStorage fallback');
      // Fallback to localStorage
      const localData = localStorage.getItem('aartis') || '[]';
      return JSON.parse(localData);
    }
  },

  async createAarti(aartiData) {
    try {
      const response = await fetch(`${AARTI_API_URL}/add/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(aartiData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data: data.data };
    } catch (error) {
      // Fallback to localStorage
      const newAarti = { ...aartiData, id: Date.now().toString() };
      const existing = JSON.parse(localStorage.getItem('aartis') || '[]');
      existing.push(newAarti);
      localStorage.setItem('aartis', JSON.stringify(existing));
      return { success: false, data: newAarti, error: error.message };
    }
  },

  async deleteAarti(id) {
    try {
      const response = await fetch(`${AARTI_API_URL}/delete/${id}/`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return { success: true };
    } catch (error) {
      // Fallback to localStorage
      const existing = JSON.parse(localStorage.getItem('aartis') || '[]');
      const filtered = existing.filter(a => a.id !== id);
      localStorage.setItem('aartis', JSON.stringify(filtered));
      return { success: false, error: error.message };
    }
  },

  async getAarti(id) {
    try {
      const response = await fetch(`${AARTI_API_URL}/get/${id}/`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      // Fallback to localStorage
      const existing = JSON.parse(localStorage.getItem('aartis') || '[]');
      const aarti = existing.find(a => a.id === id);
      return aarti ? { success: true, data: aarti } : { success: false, error: 'Aarti not found' };
    }
  }
};

// Chalisa Management API
export const chalisaAPI = {
  async testConnection() {
    try {
      const response = await fetch(`${CHALISA_API_URL}/list/`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async getAllChalisas() {
    try {
      const response = await fetch(`${CHALISA_API_URL}/list/`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      // Backend returns an array directly
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.log('Backend not available, using localStorage fallback');
      // Fallback to localStorage
      const localData = localStorage.getItem('chalisas') || '[]';
      return JSON.parse(localData);
    }
  },

  async createChalisa(chalisaData) {
    try {
      const response = await fetch(`${CHALISA_API_URL}/add/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(chalisaData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data: data.data };
    } catch (error) {
      // Fallback to localStorage
      const newChalisa = { ...chalisaData, id: Date.now().toString() };
      const existing = JSON.parse(localStorage.getItem('chalisas') || '[]');
      existing.push(newChalisa);
      localStorage.setItem('chalisas', JSON.stringify(existing));
      return { success: false, data: newChalisa, error: error.message };
    }
  },

  async deleteChalisa(id) {
    try {
      const response = await fetch(`${CHALISA_API_URL}/delete/${id}/`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return { success: true };
    } catch (error) {
      // Fallback to localStorage
      const existing = JSON.parse(localStorage.getItem('chalisas') || '[]');
      const filtered = existing.filter(c => c.id !== id);
      localStorage.setItem('chalisas', JSON.stringify(filtered));
      return { success: false, error: error.message };
    }
  },

  async getChalisa(id) {
    try {
      const response = await fetch(`${CHALISA_API_URL}/get/${id}/`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      // Fallback to localStorage
      const existing = JSON.parse(localStorage.getItem('chalisas') || '[]');
      const chalisa = existing.find(c => c.id === id);
      return chalisa ? { success: true, data: chalisa } : { success: false, error: 'Chalisa not found' };
    }
  }
};

// User Management API
export const userAPI = {
  async getAllUsers() {
    try {
      console.log('🔄 Fetching users from:', `${API_BASE_URL}/user/users/admin/`);
      const response = await fetch(`${API_BASE_URL}/user/users/admin/`);

      console.log('📊 Response status:', response.status);
      console.log('📊 Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ Successfully fetched users:', data);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('❌ Failed to connect to backend API. Error:', error.message);
      console.error('🔄 Please check if the backend server is running and accessible.');
      // Return empty array and let the UI handle the error state
      throw new Error(`Failed to load users: ${error.message}`);
    }
  },

  async deleteUser(userId) {
    try {
      console.log('🗑︝ Deleting user:', userId);
      const response = await fetch(`${API_BASE_URL}/user/users/${userId}/admin/`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Delete Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ User deleted successfully:', result);
      return { success: true, message: 'User deleted successfully' };
    } catch (error) {
      console.log('❌ Backend not available for user deletion, simulating. Error:', error.message);
      // Simulate successful deletion for demo
      return { success: true, message: 'User deleted successfully (demo mode)' };
    }
  },

  async getUserStats() {
    try {
      console.log('📈 Fetching user stats from:', `${API_BASE_URL}/user/users/stats/admin/`);
      const response = await fetch(`${API_BASE_URL}/user/users/stats/admin/`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Stats Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ Successfully fetched user stats:', data);
      return data;
    } catch (error) {
      console.error('❌ Failed to get user stats from backend. Error:', error.message);
      // Throw error instead of using fallback
      throw new Error(`Failed to load user statistics: ${error.message}`);
    }
  }
};
